
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { checkAccountLocked, logFailedLogin, getUserAgent, logSecurityEvent } from '@/utils/security';
import { enhancedOrganizationalDataService } from '@/services/enhancedOrganizationalDataService';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthProvider: Setting up auth listener...');
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, 'Session exists:', !!session);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Log authentication events securely (without sensitive data)
        if (session?.user && event === 'SIGNED_IN') {
          // Defer supabase calls to prevent deadlock
          setTimeout(async () => {
            try {
              await supabase.from('audit_logs').insert({
                user_id: session.user.id,
                action: 'login_success',
                details: {
                  timestamp: new Date().toISOString(),
                  user_agent: getUserAgent()
                }
              });

              await logSecurityEvent('login_success', 'User successfully logged in', {
                user_id: session.user.id,
                email: session.user.email
              });
            } catch (error) {
              console.error('Failed to log authentication event:', error);
            }
          }, 0);
        }
      }
    );

    // THEN check for existing session
    console.log('AuthProvider: Checking for existing session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('AuthProvider: Got session:', !!session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Check if account is locked using enhanced function
      const isLocked = await checkAccountLocked(email);
      if (isLocked) {
        await logSecurityEvent('login_blocked', 'Login attempt blocked due to account lockout', {
          email,
          reason: 'account_locked'
        });

        return {
          success: false,
          error: 'Account temporarily locked due to too many failed login attempts. Please try again later.'
        };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Log failed login attempt using enhanced function
        await logFailedLogin(email, undefined, getUserAgent());

        // Log security event with enhanced tracking
        await logSecurityEvent('login_failed', 'Failed login attempt', {
          email,
          error_type: error.message,
          timestamp: new Date().toISOString()
        });

        return {
          success: false,
          error: 'Invalid email or password'
        };
      }

      if (data.user) {
        // Log successful login with enhanced security tracking
        await logSecurityEvent('login_success', 'Successful login', {
          user_id: data.user.id,
          email: data.user.email,
          timestamp: new Date().toISOString()
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);

      // Log system error
      await logSecurityEvent('login_system_error', 'System error during login attempt', {
        email,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'An unexpected error occurred. Please try again.'
      };
    }
  };

  const signOut = async () => {
    try {
      // Clear organizational data cache before signing out
      enhancedOrganizationalDataService.clearAllCache();

      // Log sign out attempt (without sensitive data)
      if (user) {
        await supabase.from('audit_logs').insert({
          user_id: user.id,
          action: 'logout',
          details: {
            timestamp: new Date().toISOString()
          }
        });

        await logSecurityEvent('logout', 'User logged out', {
          user_id: user.id,
          email: user.email
        });
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);

      if (user) {
        await logSecurityEvent('logout_error', 'Error during logout', {
          user_id: user.id,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
    signIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
